import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { PageHeader } from "@/components/PageHelpers";
import { useProperty } from "@/contexts/PropertyContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Building2, Plus, Trash2, Calculator, ClipboardCheck, Save, ScrollText,
  AlertTriangle, ChevronRight, Mail,
} from "lucide-react";
import { toast } from "sonner";

/**
 * R143 — CAM Reconciliation admin page.
 *
 * Two workflows:
 *   1. Roster editor — add commercial units + share %; sum must ≈ 100.
 *   2. Run a period — pick date range, dry-run to preview, commit to save.
 *
 * Persisted reconciliations show up at the bottom for easy re-download.
 */
function fmtMoney(n) {
  return `$${Number(n || 0).toLocaleString(undefined,
    { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function firstOfMonth(delta = 0) {
  const d = new Date();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + delta);
  return d.toISOString().slice(0, 10);
}

function lastOfPreviousMonth() {
  const d = new Date();
  d.setUTCDate(0);   // → last day of previous month
  return d.toISOString().slice(0, 10);
}

export default function CamReconciliationPage() {
  const { propertyId, currentProperty } = useProperty();
  const [config, setConfig] = useState(null);
  const [drafts, setDrafts] = useState([]);       // in-memory unit rows
  const [savingRoster, setSavingRoster] = useState(false);
  const [period, setPeriod] = useState({
    period_start: firstOfMonth(-1),
    period_end: lastOfPreviousMonth(),
  });
  const [preview, setPreview] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [history, setHistory] = useState([]);
  const [detailOpen, setDetailOpen] = useState(null);

  const load = useCallback(async () => {
    if (!propertyId) return;
    try {
      const [{ data: cfg }, { data: hist }] = await Promise.all([
        api.get(`/admin/cam/config/${propertyId}`),
        api.get(`/admin/cam/reconciliations`, { params: { property_id: propertyId } }),
      ]);
      setConfig(cfg);
      setDrafts(cfg.cam_units || []);
      setHistory(hist.items || []);
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed to load CAM config");
    }
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  const totalShare = drafts.reduce((s, u) => s + Number(u.share_pct || 0), 0);
  const shareOk = drafts.length === 0 || Math.abs(totalShare - 100) <= 0.5;

  const addRow = () => setDrafts((p) => [...p,
    { unit: "", holder_name: "", holder_email: "", share_pct: 0, notes: "" }]);

  const editRow = (i, key, val) => setDrafts((p) =>
    p.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)));

  const removeRow = (i) => setDrafts((p) => p.filter((_, idx) => idx !== i));

  const saveRoster = async () => {
    if (!shareOk) {
      toast.error(`Shares total ${totalShare.toFixed(2)}% — must be within 0.5% of 100`);
      return;
    }
    setSavingRoster(true);
    try {
      const clean = drafts.map((r) => ({
        unit: r.unit.trim(),
        holder_name: r.holder_name.trim(),
        holder_email: (r.holder_email || "").trim() || undefined,
        share_pct: Number(r.share_pct),
        notes: (r.notes || "").trim() || undefined,
      })).filter((r) => r.unit && r.holder_name);
      await api.put(`/admin/cam/config/${propertyId}`, { cam_units: clean });
      toast.success("CAM roster saved");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message
        || apiErrorMessage(e) || "Save failed");
    } finally { setSavingRoster(false); }
  };

  const runPreview = async () => {
    if (!config?.is_configured) {
      toast.error("Configure CAM units first");
      return;
    }
    try {
      const { data } = await api.post("/admin/cam/reconcile", {
        property_id: propertyId,
        period_start: period.period_start,
        period_end: period.period_end,
        dry_run: true,
      });
      setPreview(data);
      if (data.expense_count === 0) {
        toast.warning("No CAM-flagged expenses in this window");
      } else {
        toast.success(`Previewed ${fmtMoney(data.expenses_total)} across ${data.unit_lines.length} units`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message
        || apiErrorMessage(e) || "Preview failed");
    }
  };

  const commit = async () => {
    if (!preview) return;
    setCommitting(true);
    try {
      const { data } = await api.post("/admin/cam/reconcile", {
        property_id: propertyId,
        period_start: period.period_start,
        period_end: period.period_end,
        dry_run: false,
      });
      toast.success("Reconciliation saved to ledger");
      setPreview(null);
      await load();
      setDetailOpen(data);
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Commit failed");
    } finally { setCommitting(false); }
  };

  return (
    <div data-testid="cam-page">
      <PageHeader
        eyebrow="Mixed-use properties"
        title="CAM reconciliation"
        subtitle={`Common-area maintenance splits for ${currentProperty?.name || "—"}. Configure the roster once, then reconcile monthly.`}
      />

      {/* ─── Roster ─── */}
      <Card className="border-white/10 bg-card p-5 mb-6"
            data-testid="cam-roster-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-orange-400" />
            <div className="text-sm font-semibold">Commercial unit roster</div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={
              shareOk
                ? "border-emerald-500/40 text-emerald-300"
                : "border-rose-500/40 text-rose-300"
            } data-testid="cam-share-total">
              {totalShare.toFixed(2)}% total
            </Badge>
            <Button size="sm" variant="outline" onClick={addRow}
                    className="border-white/10" data-testid="cam-add-unit">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add unit
            </Button>
            <Button size="sm" onClick={saveRoster}
                    disabled={savingRoster || !shareOk}
                    className="bg-orange-500 hover:bg-orange-600 text-black"
                    data-testid="cam-save-roster">
              <Save className="h-3.5 w-3.5 mr-1" />
              {savingRoster ? "Saving…" : "Save roster"}
            </Button>
          </div>
        </div>
        {drafts.length === 0 ? (
          <div className="text-sm text-white/50 py-6 text-center">
            No commercial units yet. Click <b>Add unit</b> to build the roster.
          </div>
        ) : (
          <div className="space-y-2" data-testid="cam-unit-list">
            {drafts.map((u, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-start"
                   data-testid={`cam-unit-row-${i}`}>
                <div className="col-span-2">
                  <Label className="text-[10px] uppercase text-white/40">Unit</Label>
                  <Input value={u.unit} onChange={(e) => editRow(i, "unit", e.target.value)}
                         placeholder="R-101" className="font-mono"
                         data-testid={`cam-unit-input-${i}`} />
                </div>
                <div className="col-span-3">
                  <Label className="text-[10px] uppercase text-white/40">Tenant</Label>
                  <Input value={u.holder_name}
                         onChange={(e) => editRow(i, "holder_name", e.target.value)}
                         placeholder="Coffee Roasters Co"
                         data-testid={`cam-holder-name-${i}`} />
                </div>
                <div className="col-span-3">
                  <Label className="text-[10px] uppercase text-white/40">Billing email</Label>
                  <Input value={u.holder_email || ""} type="email"
                         onChange={(e) => editRow(i, "holder_email", e.target.value)}
                         placeholder="ap@tenant.co"
                         data-testid={`cam-holder-email-${i}`} />
                </div>
                <div className="col-span-2">
                  <Label className="text-[10px] uppercase text-white/40">Share %</Label>
                  <Input type="number" step="0.01" min="0" max="100"
                         value={u.share_pct}
                         onChange={(e) => editRow(i, "share_pct", e.target.value)}
                         className="tabular-nums"
                         data-testid={`cam-share-input-${i}`} />
                </div>
                <div className="col-span-1 pt-6 flex justify-end">
                  <Button size="sm" variant="ghost" onClick={() => removeRow(i)}
                          className="text-rose-300 hover:bg-rose-500/10 h-8 w-8 p-0"
                          data-testid={`cam-remove-${i}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
                <div className="col-span-1 pt-6 text-[11px] text-white/40 text-right">
                  #{i + 1}
                </div>
              </div>
            ))}
          </div>
        )}
        {!shareOk && (
          <div className="mt-3 text-xs text-rose-300 flex items-center gap-1"
               data-testid="cam-share-warning">
            <AlertTriangle className="h-3 w-3" />
            Shares must sum to 100 % (within 0.5% drift). Currently {totalShare.toFixed(2)}%.
          </div>
        )}
      </Card>

      {/* ─── Run period ─── */}
      <Card className="border-white/10 bg-card p-5 mb-6" data-testid="cam-run-card">
        <div className="flex items-center gap-2 mb-3">
          <Calculator className="h-4 w-4 text-sky-400" />
          <div className="text-sm font-semibold">Run a reconciliation</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-[10px] uppercase text-white/40">Period start</Label>
            <Input type="date" value={period.period_start}
                   onChange={(e) => setPeriod({ ...period, period_start: e.target.value })}
                   data-testid="cam-period-start" />
          </div>
          <div>
            <Label className="text-[10px] uppercase text-white/40">Period end</Label>
            <Input type="date" value={period.period_end}
                   onChange={(e) => setPeriod({ ...period, period_end: e.target.value })}
                   data-testid="cam-period-end" />
          </div>
          <div className="flex items-end gap-2">
            <Button onClick={runPreview}
                    className="border-white/10 flex-1" variant="outline"
                    data-testid="cam-preview-btn">
              <Calculator className="h-3.5 w-3.5 mr-1" /> Preview
            </Button>
            <Button onClick={commit} disabled={!preview || committing}
                    className="bg-emerald-500 hover:bg-emerald-600 text-black flex-1"
                    data-testid="cam-commit-btn">
              <ClipboardCheck className="h-3.5 w-3.5 mr-1" />
              {committing ? "Saving…" : "Commit"}
            </Button>
          </div>
        </div>
        {preview && (
          <div className="mt-4 rounded-lg border border-sky-500/20 bg-sky-500/[0.05] p-4"
               data-testid="cam-preview-result">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-sky-300">Preview</div>
                <div className="text-2xl font-semibold tabular-nums">
                  {fmtMoney(preview.expenses_total)}
                </div>
                <div className="text-[11px] text-white/50">
                  {preview.expense_count} CAM-flagged expense{preview.expense_count === 1 ? "" : "s"}
                </div>
              </div>
              <Badge variant="outline" className="border-sky-500/40 text-sky-300">
                {preview.period_start} → {preview.period_end}
              </Badge>
            </div>
            <table className="w-full text-sm">
              <thead className="text-white/60">
                <tr>
                  <th className="text-left px-2 py-1">Unit</th>
                  <th className="text-left px-2 py-1">Tenant</th>
                  <th className="text-right px-2 py-1">Share</th>
                  <th className="text-right px-2 py-1">Bill</th>
                </tr>
              </thead>
              <tbody>
                {preview.unit_lines.map((l) => (
                  <tr key={l.unit} className="border-t border-white/5"
                      data-testid={`cam-preview-line-${l.unit}`}>
                    <td className="px-2 py-1.5 font-mono">{l.unit}</td>
                    <td className="px-2 py-1.5">{l.holder_name}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-white/60">
                      {l.share_pct.toFixed(2)}%
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums font-medium">
                      {fmtMoney(l.billed_amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ─── History ─── */}
      <Card className="border-white/10 bg-card p-0 overflow-hidden"
            data-testid="cam-history-card">
        <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
          <ScrollText className="h-4 w-4 text-orange-400" />
          <div className="text-sm font-semibold">Prior reconciliations</div>
          <Badge variant="outline" className="border-white/10 text-white/60">
            {history.length}
          </Badge>
        </div>
        {history.length === 0 ? (
          <div className="p-6 text-sm text-white/50 text-center"
               data-testid="cam-history-empty">
            No prior reconciliations for this property yet.
          </div>
        ) : (
          <ul className="divide-y divide-white/5">
            {history.map((r) => (
              <li key={r.id}
                  className="px-4 py-3 flex items-center gap-3 text-sm hover:bg-white/[0.02] cursor-pointer"
                  onClick={() => setDetailOpen(r)}
                  data-testid={`cam-history-${r.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-white">
                    {r.period_start} → {r.period_end}
                  </div>
                  <div className="text-[11px] text-white/40">
                    Generated {new Date(r.generated_at).toLocaleString()} by {r.generated_by_name || "—"}
                  </div>
                </div>
                <div className="tabular-nums text-right">
                  <div className="font-medium">{fmtMoney(r.expenses_total)}</div>
                  <div className="text-[11px] text-white/40">
                    {r.unit_lines?.length ?? 0} units
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-white/40" />
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* ─── Detail drilldown ─── */}
      <Dialog open={!!detailOpen} onOpenChange={(o) => !o && setDetailOpen(null)}>
        <DialogContent className="max-w-3xl" data-testid="cam-detail-modal">
          <DialogHeader>
            <DialogTitle>
              CAM reconciliation · {detailOpen?.period_start} → {detailOpen?.period_end}
            </DialogTitle>
          </DialogHeader>
          {detailOpen && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div>
                  <div className="text-[10px] uppercase text-white/40">Total</div>
                  <div className="text-xl font-semibold tabular-nums">
                    {fmtMoney(detailOpen.expenses_total)}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-white/40">Units billed</div>
                  <div className="text-xl font-semibold tabular-nums">
                    {detailOpen.unit_lines?.length ?? 0}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-white/40">Expenses</div>
                  <div className="text-xl font-semibold tabular-nums">
                    {detailOpen.expense_count ?? 0}
                  </div>
                </div>
              </div>

              {/* R144a — email statements batch action */}
              <div className="flex items-center justify-between rounded-lg border border-emerald-500/20 bg-emerald-500/[0.05] px-3 py-2">
                <div className="min-w-0">
                  <div className="text-sm font-medium">Statements to tenants</div>
                  <div className="text-[11px] text-white/60">
                    {detailOpen.statements_sent_at
                      ? <>Sent {new Date(detailOpen.statements_sent_at).toLocaleString()} · {detailOpen.statements_send_counts?.sent ?? 0} sent · {detailOpen.statements_send_counts?.queued ?? 0} queued · {detailOpen.statements_send_counts?.skipped ?? 0} skipped</>
                      : "Batch-email every tenant their itemised PDF."}
                  </div>
                </div>
                <Button size="sm"
                        onClick={async () => {
                          try {
                            const { data } = await api.post(
                              `/admin/cam/reconciliations/${detailOpen.id}/send-statements`
                            );
                            toast.success(
                              `Sent ${data.sent} · queued ${data.queued} · skipped ${data.skipped}`
                            );
                            // Re-fetch the detail so the "sent at" pill updates.
                            const r = await api.get(`/admin/cam/reconciliations/${detailOpen.id}`);
                            setDetailOpen(r.data);
                            await load();
                          } catch (e) {
                            toast.error(e?.response?.data?.detail?.message
                                          || apiErrorMessage(e) || "Send failed");
                          }
                        }}
                        className="bg-emerald-500 hover:bg-emerald-600 text-black shrink-0"
                        data-testid="cam-send-statements">
                  <Mail className="h-3.5 w-3.5 mr-1" />
                  {detailOpen.statements_sent_at ? "Resend all" : "Email statements"}
                </Button>
              </div>

              <table className="w-full text-sm">
                <thead className="text-white/60">
                  <tr>
                    <th className="text-left px-2 py-1">Unit</th>
                    <th className="text-left px-2 py-1">Tenant</th>
                    <th className="text-left px-2 py-1">Billing email</th>
                    <th className="text-right px-2 py-1">Share</th>
                    <th className="text-right px-2 py-1">Bill</th>
                    <th className="text-right px-2 py-1">PDF</th>
                  </tr>
                </thead>
                <tbody>
                  {(detailOpen.unit_lines || []).map((l) => (
                    <tr key={l.unit} className="border-t border-white/5">
                      <td className="px-2 py-1.5 font-mono">{l.unit}</td>
                      <td className="px-2 py-1.5">{l.holder_name}</td>
                      <td className="px-2 py-1.5 text-white/60">{l.holder_email || "—"}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-white/60">
                        {l.share_pct?.toFixed?.(2) ?? l.share_pct}%
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums font-medium">
                        {fmtMoney(l.billed_amount)}
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              const r = await fetch(
                                `${process.env.REACT_APP_BACKEND_URL}/api/admin/cam/reconciliations/${detailOpen.id}/statement/${encodeURIComponent(l.unit)}.pdf`,
                                { credentials: "include" }
                              );
                              if (!r.ok) throw new Error(`http ${r.status}`);
                              const blob = await r.blob();
                              const a = document.createElement("a");
                              a.href = URL.createObjectURL(blob);
                              a.download = `cam-${l.unit}-${detailOpen.period_start}.pdf`;
                              a.click();
                              URL.revokeObjectURL(a.href);
                            } catch { toast.error("PDF download failed"); }
                          }}
                          className="text-orange-300 hover:text-orange-200 text-xs"
                          data-testid={`cam-download-pdf-${l.unit}`}>
                          Download
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
